import i18next from "i18next";
import { VehicleMassiveUpdateRequestDTO } from "src/api";
import { isInvalidNumber } from "src/utils";
import _ from "lodash";
import { NOLEGGIATORI_QUOTATORE } from "src/utils/constants";

export const FIELDS_WITH_TOGGLE = [
  'limited_visibility',
  'data_disp',
  'promo_note',
  'extra_sconto_perc',
  'prezzo_listino',
  'visibility_tag',
  'lista_noleggiatori_quotatore',
] as const;

// Campi che la modifica massiva può svuotare (inviati in fields_to_clear)
export const CLEARABLE_FIELDS = ['data_disp', 'promo_note'] as const;

export const listaNoleggiatori = () => ([
  { noleggiatore: NOLEGGIATORI_QUOTATORE.arval.name, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.ayvens.name, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.leasys.name, extra_sconto_perc: null },
  { noleggiatore: NOLEGGIATORI_QUOTATORE.alphabet.name, extra_sconto_perc: null },
] as const);

export const validateNumericField = (
  values: any,
  errors: any,
  fieldName: string,
  allowNil: boolean = false,
): void => {
  if (!values[`${fieldName}_enabled`]) return;

  if (!values[fieldName] && !allowNil) {
    errors[fieldName] = i18next.t('mandatory');
    return;
  }

  if (isInvalidNumber(values[fieldName])) {
    errors[fieldName] = i18next.t('numeric_values_allowed');
  }
};

export const validateRequiredField = (
  values: any,
  errors: any,
  fieldName: string
): void => {
  let value = values[fieldName];
  const isEnabled = values[`${fieldName}_enabled`];

  if (values[`${fieldName}_clear`]) return;

  if (_.isString(value)) value = value?.trim();

  if (isEnabled && _.isEmpty(value)) {
    errors[fieldName] = i18next.t('mandatory');
  }
};

export const validateNoleggiatori = (values: any, errors: any): void => {
  if (!values.extra_sconto_perc_enabled) return;

  const noleggiatoriErrors = (values.lista_noleggiatori_quotatore || [])
    .reduce((acc, item, idx) => {
      if (_.isNil(item.extra_sconto_perc)) return acc;
      if (!isInvalidNumber(item.extra_sconto_perc)) return acc;

      return {
        ...acc,
        [idx]: { extra_sconto_perc: i18next.t('numeric_values_allowed') },
      };
    }, {});

  if (!_.isEmpty(noleggiatoriErrors)) {
    errors.lista_noleggiatori_quotatore = noleggiatoriErrors;
  }
};

export const buildBasePayload = (values: any): Partial<VehicleMassiveUpdateRequestDTO> => {
  const fields_to_clear = CLEARABLE_FIELDS.filter(
    (field) => values[`${field}_enabled`] && values[`${field}_clear`]
  );

  const payload = FIELDS_WITH_TOGGLE.reduce((payload, field) => {
    if (!values[`${field}_enabled`]) return payload;
    if ((fields_to_clear as string[]).includes(field)) return payload;

    if (field === 'limited_visibility') {
      return { ...payload, [field]: values[field]?.map((x: any) => x.value) };
    }

    return { ...payload, [field]: values[field] };
  }, {});

  return _.isEmpty(fields_to_clear) ? payload : { ...payload, fields_to_clear };
};

export const buildNoleggiatoriPayload = (values: any): Pick<VehicleMassiveUpdateRequestDTO, 'lista_noleggiatori_quotatore'> | {} => {
  if (!values.extra_sconto_perc_enabled) return {};

  const lista_noleggiatori_quotatore = (values.lista_noleggiatori_quotatore || [])
    .filter((item: any) => {
      return item.noleggiatore
        && !_.isNil(item.extra_sconto_perc)
        && !isInvalidNumber(item.extra_sconto_perc)
        && '' !== String(item.extra_sconto_perc)?.trim()
    })
    .map(({ noleggiatore, extra_sconto_perc }: any) => ({ noleggiatore, extra_sconto_perc }));

  return { lista_noleggiatori_quotatore };
};

export const sanitizePayload = (
  payload: Partial<VehicleMassiveUpdateRequestDTO>
): Partial<VehicleMassiveUpdateRequestDTO> => {
  const FIELDS_TO_OMIT_IF_EMPTY: (keyof VehicleMassiveUpdateRequestDTO)[] = [
    'visibility_tag',
    'data_disp',
  ];
  const FIELDS_TO_OMIT_IF_NIL: (keyof VehicleMassiveUpdateRequestDTO)[] = [
    'extra_sconto_perc',
    'visibility_tag',
    'data_disp',
  ];

  let sanitized = { ...payload };

  for (const field of FIELDS_TO_OMIT_IF_EMPTY) {
    if (_.isEmpty(sanitized[field])) {
      delete sanitized[field]
    }
  }

  for (const field of FIELDS_TO_OMIT_IF_NIL) {
    if (_.isNil(sanitized[field])) {
      delete sanitized[field]
    }
  }

  if (String(payload.extra_sconto_perc)?.trim() === '') {
    delete sanitized.extra_sconto_perc;
  }

  if (String(payload.prezzo_listino)?.trim() === '') {
    delete sanitized.prezzo_listino;
  }

  return sanitized;
};
